import { getConfig } from "./configService";
import { setAccessToken, setRefreshToken } from "./api";
import type { ScTokenPair } from "./authService";

/*
 * ВХОД ПО ЛИЦУ — `sc_atyrau_backend`, CompreFace за ним.
 *
 * Отдельно от `authApi`, потому что тому нечем передать причину отказа: он
 * бросает «Request failed» со статусом, а здесь важен машинный `code`.
 * `invalid_credentials` — не узнали (по любой из причин, которые бэкенд
 * нарочно не различает), `face_auth_unavailable` — CompreFace лёг, и честный
 * совет человеку — войти паролем, а не смотреть в камеру ещё раз.
 *
 * Luna здесь нет и не будет: её код удалён в 17b1a46, вход по лицу у Атырау
 * только через CompreFace, и сверку делает бэкенд, а не планшет.
 */

export class FaceLoginError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number,
  ) {
    super(message);
    this.name = "FaceLoginError";
  }
}

function authUrl(path: string): string {
  return `${getConfig().AUTH_API_URL}${path}`;
}

/** Логин + кадр (data URL) → пара токенов. Токены кладутся сразу, как у пароля. */
export async function loginWithFaceToScBackend(
  username: string,
  image: string,
): Promise<ScTokenPair> {
  let response: Response;
  try {
    response = await fetch(authUrl("/auth/face/login"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, image }),
    });
  } catch {
    throw new FaceLoginError("Нет связи с сервером", null, 0);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      code?: unknown;
      message?: unknown;
    };
    const code = typeof body.code === "string" ? body.code : null;
    const message =
      typeof body.message === "string"
        ? body.message
        : response.status === 429
          ? "Слишком много попыток. Подождите минуту."
          : "Не удалось войти по лицу";
    throw new FaceLoginError(message, code, response.status);
  }

  const data = (await response.json()) as ScTokenPair;
  if (data.accessToken) setAccessToken(data.accessToken);
  if (data.refreshToken) setRefreshToken(data.refreshToken);
  return data;
}

/**
 * Включён ли вход по лицу на этом стенде. Не ответил — считаем, что нет:
 * вкладка, которая на первой попытке скажет «недоступно», хуже её отсутствия.
 */
export async function fetchFaceLoginAvailable(): Promise<boolean> {
  try {
    const response = await fetch(authUrl("/auth/methods"));
    if (!response.ok) return false;
    const body = (await response.json()) as { face?: unknown };
    return body.face === true;
  } catch {
    return false;
  }
}
